import { useEffect } from 'react'
import type { ChartManager } from '../core/ChartManager'
import type { EdgeTheme } from '../types/chart'
import type { Period } from '../utils/resolution'
import type { ChartSettings } from '../components/SettingsModal'
import type { ChartInteractionSettings } from '../types/interaction'
import type { DeepPartial, Styles } from 'klinecharts'
import { ensureFixedPriceAxis } from '../utils/fixedPriceAxis'
import { candleStylesForChartType } from '../utils/chartTypeStyles'
import { pctToAlpha } from '../utils/tpoSettings'
import { hexToRgba } from '../utils/color'

/**
 * Colour for the series line, honouring the TPO panel's own Line row.
 *
 * The TPO chart style renders through klinecharts' `area` series, whose colour
 * normally comes from the global `lineColor` — a setting whose control only
 * appears in the Line style group, so from the TPO panel it was unreachable and
 * the panel's Line row did nothing. Routing it here makes that row live.
 */
function tpoLineColor(chartType: string, settings: ChartSettings): string {
  if (chartType !== 'tpo') return settings.lineColor
  if (!settings.tpo.line.visible) return 'transparent'
  return hexToRgba(settings.tpo.line.color, pctToAlpha(settings.tpo.lineOpacity))
}
import { TPO_INDICATOR_NAME } from '../utils/tpoIndicator'
import { HEIKIN_ASHI_INDICATOR_NAME, heikinAshiStyleOf } from '../utils/heikinAshiIndicator'
import { defaultAxisTextColor, effectiveGridMode, styleGroupOf } from '../components/chartSettings'
import { chartColorDefaults, effectiveColor, type ThemedColorKey } from '../utils/themedColors'
import { CANDLE_PANE_ID } from '../types/chart'

type Chart = ReturnType<ChartManager['getChart']>

function paintKlineContainer(root: HTMLElement | null, background: string): void {
  const kcContainer = root?.firstElementChild as HTMLElement | null
  if (kcContainer) kcContainer.style.background = background
}

/**
 * Consolidates all chart visual style application into one hook:
 * - Theme sync (EdgeTheme prop)
 * - Custom styles override (DeepPartial<Styles> prop)
 * - Settings apply (candle colors, grid, crosshair, separator, background)
 * - Fixed price axis toggle
 */
export function useChartStyles(
  managerRef: React.RefObject<ChartManager | null>,
  chartContainerRef: React.RefObject<HTMLDivElement | null>,
  chartInstance: Chart | null,
  theme: EdgeTheme | undefined,
  /** Bumps on app-theme change; re-runs the color-apply effect so theme colors
   *  repaint even when `settings` is unchanged. */
  themeVersion: number | undefined,
  customStyles: DeepPartial<Styles> | undefined,
  settings: ChartSettings,
  chartType: string,
  /** The chart's own bar interval. TPO needs it to clamp the bracket size to at
   *  least one bar — a klinecharts `draw` never receives the period. */
  period: Period | undefined,
  globalSettings: Partial<ChartSettings> | undefined,
  /** Per-user global interaction settings — see `ChartInteractionSettings`. */
  interaction: ChartInteractionSettings,
  setSettings: React.Dispatch<React.SetStateAction<ChartSettings>>,
  /** True while the host is drawing its custom last-price tag (price + bar
   *  countdown). Hides the NATIVE last-price tag text so the two don't double up;
   *  a live bar only, so the native tag returns on historical / drilled charts. */
  barCountdownActive: boolean,
  /** Extra px the canvas-drawn OHLC tooltip shifts down/right so it clears the
   *  floating toolbar (top) and the floating drawing bar (left). */
  tooltipInset?: { top: number; left: number },
): void {
  // Sync theme prop
  useEffect(() => {
    if (theme) managerRef.current?.setTheme(theme)
  }, [managerRef, theme])

  // Global settings sync — a grid-level Settings modal (dashboard/scanner
  // multi-cell layouts) pushes one settings object to every cell through the
  // `globalSettings` prop. Merging it into internal state here is what makes
  // the style effects below (axis visibility, colors, grid, …) actually
  // re-run; without it the prop lands and silently does nothing.
  useEffect(() => {
    if (!globalSettings) return
    setSettings((prev) => {
      // The prop often arrives as a fresh object echoing values already in
      // state (settings round-trip through the host's panel state). Bail on
      // per-key identity so the whole-`settings`-keyed style effects below
      // don't re-apply the full style bundle for a no-op merge.
      const keys = Object.keys(globalSettings) as Array<keyof ChartSettings>
      if (keys.every((k) => Object.is(prev[k], globalSettings[k]))) return prev
      return { ...prev, ...globalSettings }
    })
  }, [globalSettings, setSettings])

  // Sync custom styles override
  useEffect(() => {
    if (customStyles) managerRef.current?.setTheme(customStyles)
  }, [managerRef, customStyles])

  // Apply settings to chart (candle colors, grid, crosshair, separator, background)
  useEffect(() => {
    const chart = managerRef.current?.getChart()
    if (!chart) return

    // Resolve each color to its EFFECTIVE value: the themed color the `theme`
    // prop carries (buildChartTheme resolves the app's --chart-* tokens to
    // concrete hex), unless the user explicitly picked this one in the settings
    // modal — `settings.customizedColors` records that intent. We ALWAYS push
    // explicit colors: relying on setTheme's merge alone left candle bodies
    // unfilled and the volume mismatched.
    const t = theme && typeof theme === 'object' ? (theme as DeepPartial<Styles>) : undefined
    const defaults = chartColorDefaults(t, settings)
    const eff = (key: ThemedColorKey): string =>
      effectiveColor(key, settings[key], defaults, settings.customizedColors)

    const upColor = eff('candleUpColor')
    const downColor = eff('candleDownColor')
    // Border and wick DEFAULT to the effective body (chartColorDefaults points
    // them at it) so the outline can't drift from the fill on its own, but stay
    // individually settable — the modal's Border / Wicks rows only win once the
    // user has actually picked one.
    const upBorderColor = eff('candleBorderUpColor')
    const downBorderColor = eff('candleBorderDownColor')
    const upWickColor = eff('wickUpColor')
    const downWickColor = eff('wickDownColor')
    const gridHorizontalColor = eff('gridHorizontalColor')
    const gridVerticalColor = eff('gridVerticalColor')
    const crosshairColor = eff('crosshairColor')
    const crosshairLabelBg = eff('crosshairLabelBg')
    const separatorColor = eff('separatorColor')
    const gridMode = effectiveGridMode(settings)
    // Axis text colour: the user's textColor when set, else the canvas-aware
    // default (#8f8fa8 on dark canvases, #525252 on light — see
    // defaultAxisTextColor). An EMPTY-string textColor (persisted by panels
    // saved before that default existed) is treated as unset so it recovers to
    // the default rather than falling through to the near-white --chart-text
    // token.
    const axisTextColor =
      settings.textColor?.trim() || defaultAxisTextColor(settings.backgroundColor)
    // Dashed [2,2] is the themes' grid look (the default); dotted rides the
    // dashed type with a tighter pattern, klinecharts-style.
    const gridDash =
      settings.gridLineStyle === 'solid'
        ? { style: 'solid' as const, dashedValue: [2, 2] }
        : settings.gridLineStyle === 'dotted'
          ? { style: 'dashed' as const, dashedValue: [1, 3] }
          : { style: 'dashed' as const, dashedValue: [2, 2] }

    chart.setStyles({
      candle: {
        ...candleStylesForChartType(chartType, tpoLineColor(chartType, settings)),
        bar: {
          upColor,
          downColor,
          // Eye-hidden borders/wicks render transparent — klinecharts has no
          // per-element show flag on the candle bar.
          upBorderColor: settings.showCandleBorder === false ? 'transparent' : upBorderColor,
          downBorderColor: settings.showCandleBorder === false ? 'transparent' : downBorderColor,
          upWickColor: settings.showCandleWicks === false ? 'transparent' : upWickColor,
          downWickColor: settings.showCandleWicks === false ? 'transparent' : downWickColor,
          // Doji (open === close) is neutral, not bearish — render it gray to
          // match the themes + indicator bars rather than the red down color.
          noChangeColor: '#758696',
          noChangeBorderColor: '#758696',
          noChangeWickColor: '#758696',
        },
        // Per-chart visibility toggles. Deep-merged by klinecharts, so the
        // high/low/last config from the theme is preserved aside from `show`.
        priceMark: {
          high: { show: !settings.hideHighLow },
          low: { show: !settings.hideHighLow },
          // Hide only the label tag; the last-price line stays. Also hidden while
          // the host draws its custom tag (price + countdown) so they don't double
          // up — but only for a live bar, so the native tag returns on historical
          // / drilled charts where there's no countdown.
          last: {
            text: { show: !settings.hideLastPriceLabel && !barCountdownActive },
            // The line has its own toggle; hiding it leaves the label alone.
            line: { show: !settings.hideLastPriceLine },
          },
        },
        // Base offsets come from the themes (offsetTop: 4); the inset pushes
        // the OHLC row clear of the floating toolbar / drawing bar overlays.
        tooltip: {
          offsetTop: 4 + (tooltipInset?.top ?? 0),
          offsetLeft: 4 + (tooltipInset?.left ?? 0),
        },
      },
      grid: {
        show: gridMode !== 'none',
        horizontal: {
          color: gridHorizontalColor,
          show: gridMode === 'both' || gridMode === 'horizontal',
          ...gridDash,
        },
        vertical: {
          color: gridVerticalColor,
          show: gridMode === 'both' || gridMode === 'vertical',
          ...gridDash,
        },
      },
      crosshair: {
        show: settings.showCrosshair !== false,
        // Which lines draw: 'horizontal' is the price line, 'vertical' the
        // time line — same segmented model as the grid.
        horizontal: {
          show: (settings.crosshairMode ?? 'both') !== 'vertical',
          line: { color: crosshairColor, style: settings.crosshairStyle },
          text: { backgroundColor: crosshairLabelBg },
        },
        vertical: {
          show: (settings.crosshairMode ?? 'both') !== 'horizontal',
          line: { color: crosshairColor, style: settings.crosshairStyle },
          text: { backgroundColor: crosshairLabelBg },
        },
      },
      separator: {
        color: separatorColor,
      },
      // Axis tick-text colour. ALWAYS emitted when resolvable: klinecharts
      // MERGES style overrides, so omitting the key on reset would leave the
      // previous override in place forever (the visible-flag lesson). Empty
      // setting falls back to the live --chart-text token — the theme's value.
      ...(axisTextColor
        ? {
            xAxis: { tickText: { color: axisTextColor } },
            yAxis: { tickText: { color: axisTextColor } },
          }
        : {}),
      // Volume bars use the FULL effective candle color so a separate volume
      // pane matches the candles exactly. The candle-pane OVERLAY variant
      // (VOL_OVERLAY) applies its own translucency so it recedes behind the
      // price candles without shifting hue — see volumeIndicator.ts.
      indicator: {
        bars: [
          {
            upColor,
            downColor,
            noChangeColor: '#758696',
          },
        ],
      },
    } as Parameters<typeof chart.setStyles>[0])

    chart.setTimezone(settings.timezone)

    // Heikin Ashi paints its own candles, so the resolved candle colours and
    // the border / wick / hollow flags ride to its `draw` on extendData (the TPO
    // pattern). A no-op while the style is not active — overrideIndicator
    // ignores names that are not mounted — so no chart-type guard is needed.
    chart.overrideIndicator({
      name: HEIKIN_ASHI_INDICATOR_NAME,
      extendData: { heikin: heikinAshiStyleOf(settings, theme, chartType) },
    } as Parameters<typeof chart.overrideIndicator>[0])

    // Sync background on klinecharts-created container
    paintKlineContainer(chartContainerRef.current, settings.backgroundColor)
    // Keep the drawing grab-handle hollow fill matching the canvas background.
    managerRef.current?.setOverlayHandleFill(settings.backgroundColor)
  }, [
    managerRef,
    chartContainerRef,
    settings,
    chartType,
    barCountdownActive,
    tooltipInset?.top,
    tooltipInset?.left,
    // `theme` feeds chartColorDefaults above. Hosts memoize it on themeVersion
    // (use-kline-chart-theme), so this dep is a no-op there — it exists for
    // hosts whose theme identity can change without a themeVersion bump.
    theme,
    themeVersion,
  ])

  // Wheel-zoom anchor (cursor vs. pin-to-last-bar) — global per-user setting.
  useEffect(() => {
    managerRef.current?.setZoomAnchor(interaction.zoomAnchor)
  }, [managerRef, interaction.zoomAnchor, chartInstance])

  // Wheel action (zoom vs. pan) — global per-user setting.
  useEffect(() => {
    managerRef.current?.setWheelAction(interaction.wheelAction)
  }, [managerRef, interaction.wheelAction, chartInstance])

  // Drag action (pan vs. rubber-band zoom region) — global per-user setting.
  useEffect(() => {
    managerRef.current?.setDragAction(interaction.dragAction)
  }, [managerRef, interaction.dragAction, chartInstance])

  // Smooth motion: gliding wheel zoom and Go to latest, with the price scale
  // easing during both (on unless turned off).
  useEffect(() => {
    managerRef.current?.setSmoothMotion(interaction.smoothMotion ?? true)
  }, [managerRef, interaction.smoothMotion, chartInstance])

  // Reset zoom on ticker change (off by default) — global per-user setting.
  useEffect(() => {
    managerRef.current?.setResetZoomOnSymbolChange(interaction.resetZoomOnSymbolChange ?? false)
  }, [managerRef, interaction.resetZoomOnSymbolChange, chartInstance])

  // Reset-zoom policy per timeframe class (absent = today's fixed reset).
  useEffect(() => {
    managerRef.current?.setResetZoomPolicy(interaction.resetZoom)
  }, [managerRef, interaction.resetZoom, chartInstance])

  // Hide price & time scales (crosshair labels stay at the chart edges)
  useEffect(() => {
    managerRef.current?.setHideAxisScale(settings.hideAxisScale)
  }, [managerRef, settings.hideAxisScale, chartInstance])

  // Right margin — empty bars kept clear of the newest candle.
  useEffect(() => {
    managerRef.current?.setRightMarginBars(settings.rightMarginBars ?? 3)
  }, [managerRef, settings.rightMarginBars, chartInstance])

  // Vertical margins — klinecharts pane gap takes fractions of pane height.
  useEffect(() => {
    const chart = managerRef.current?.getChart()
    if (!chart) return
    chart.setPaneOptions({
      id: CANDLE_PANE_ID,
      gap: {
        top: Math.max(0, settings.topMarginPct ?? 10) / 100,
        bottom: Math.max(0, settings.bottomMarginPct ?? 10) / 100,
      },
    } as Parameters<typeof chart.setPaneOptions>[0])
  }, [managerRef, settings.topMarginPct, settings.bottomMarginPct, chartInstance])

  /**
   * Price-axis auto-fit scope (candles only vs. candles + on-pane indicators).
   *
   * The TPO chart style is EXEMPT from 'candles'. In that mode the candles are a
   * bare close-price line and the profile IS the pane's content, not a
   * supplementary overlay — so "fit candles only" scales the axis to the close
   * line and clips the profile, which is exactly what it did (measured: axis top
   * 4.48 against a session high of 4.55). The setting exists to stop a distant EMA
   * or pivot squashing the price scale; the pane's primary series is not that.
   */
  useEffect(() => {
    // Heikin Ashi is exempt for the same reason: its candles are figures on an
    // internal indicator, and 'candles' would fit the axis to the hidden native
    // series' closes and clip them.
    const group = styleGroupOf(chartType)
    const scope = group === 'tpo' || group === 'heikin' ? 'indicators' : settings.priceAxisScope
    managerRef.current?.setPriceAxisScope(scope)
  }, [managerRef, settings.priceAxisScope, chartType, chartInstance])

  // Price-axis scale style: type (regular / percent / log), invert, position.
  useEffect(() => {
    managerRef.current?.setPriceScaleStyle({
      type: settings.priceScaleType,
      reverse: settings.priceScaleInvert,
      position: settings.priceScalePosition,
    })
  }, [
    managerRef,
    settings.priceScaleType,
    settings.priceScaleInvert,
    settings.priceScalePosition,
    chartInstance,
  ])

  /**
   * Fixed price axis toggle.
   *
   * The pane's axis is selected by NAME through `axisOptions` — klinecharts
   * reads `PaneOptions.axisOptions.name` and ignores any other key, so this
   * must not be spelled `axis` (that shape type-checks only behind a cast and
   * is silently dropped by the pane's option merge).
   *
   * The fixed grid computes ticks in raw price space with a top-down coord
   * mapping, so it is only selected for a regular, non-inverted scale: a
   * log/percentage range is in transformed space and `reverse` flips the
   * mapping, neither of which reaches the axis template.
   */
  useEffect(() => {
    const chart = managerRef.current?.getChart()
    if (!chart) return
    const fixedApplies =
      settings.fixedPriceGrid &&
      settings.fixedPriceIncrement > 0 &&
      settings.priceScaleType === 'normal' &&
      !settings.priceScaleInvert
    const name = fixedApplies ? ensureFixedPriceAxis(settings.fixedPriceIncrement) : 'default'
    chart.setPaneOptions({ id: CANDLE_PANE_ID, axisOptions: { name } })
  }, [
    managerRef,
    settings.fixedPriceGrid,
    settings.fixedPriceIncrement,
    settings.priceScaleType,
    settings.priceScaleInvert,
    chartInstance,
  ])

  // Pattern detection (support/resistance) — driven from ChartSettings, applied to
  // the engine on change. Off by default; recomputes overlays in place.
  useEffect(() => {
    managerRef.current?.setPatternDetection({
      enabled: settings.patternDetectionEnabled,
      minConfidence: settings.patternDetectionMinConfidence,
    })
  }, [
    managerRef,
    settings.patternDetectionEnabled,
    settings.patternDetectionMinConfidence,
    chartInstance,
  ])

  // Auto Fibonacci (Beta) — same shape as pattern detection: settings in,
  // engine re-anchors (or clears) the locked retracement in place.
  useEffect(() => {
    managerRef.current?.setAutoFib({
      enabled: settings.autoFibEnabled,
      swingMult: settings.autoFibSwingMult,
    })
  }, [managerRef, settings.autoFibEnabled, settings.autoFibSwingMult, chartInstance])

  // Auto trendlines (Beta).
  useEffect(() => {
    managerRef.current?.setAutoTrendlines({
      enabled: settings.autoTrendlinesEnabled,
      maxPerDirection: settings.autoTrendlinesMaxPerSide,
    })
  }, [managerRef, settings.autoTrendlinesEnabled, settings.autoTrendlinesMaxPerSide, chartInstance])

  // Shared touch input (wicks / bodies) for every auto-analysis detector.
  useEffect(() => {
    managerRef.current?.setAutoAnalysisTouchInput(settings.autoAnalysisTouchInput)
  }, [managerRef, settings.autoAnalysisTouchInput, chartInstance])

  // TPO style — pushed onto the internal TPO indicator's extendData rather than
  // through setStyles, because the profile is painted by a custom `draw` that
  // reads its own settings object. overrideIndicator is a no-op when the TPO
  // chart style is not active (no such indicator exists), so this needs no
  // chart-type guard. Keyed on the settings object identity: SettingsModal always
  // emits a fresh snapshot, so a new `tpo` object means something changed.
  useEffect(() => {
    const chart = managerRef.current?.getChart()
    if (!chart) return
    chart.overrideIndicator({
      name: TPO_INDICATOR_NAME,
      extendData: {
        // Profile session is DERIVED from Session & data's extended-hours
        // toggle — single source of truth (the standalone "Profile session"
        // control was removed 2026-08-01): ETH shown ⇒ the profile includes
        // it; ETH off ⇒ regular-hours profile. Any persisted
        // `tpo.profileSession` value is deliberately overridden here.
        tpo: { ...settings.tpo, profileSession: settings.extendedHours ? 'all' : 'regular' },
        // periodMs rides along so the draw can clamp the bracket size to at
        // least one bar — a klinecharts `draw` never sees the period otherwise.
        periodMs: period?.ms,
      },
    } as Parameters<typeof chart.overrideIndicator>[0])
  }, [managerRef, settings.tpo, settings.extendedHours, period, chartInstance])
}
